import React, { useId } from "react";
import { Alert, Box, Button, ButtonGroup, Stack, Typography, useTheme } from "@mui/material";
import { CssTextField } from "../../components/TextField";
import { BASES, basisRateId } from "./reservationDraft";
import { focusRing, formatMoney } from "./presentation";
import { CreateBookingButton, MOBILE_BAR_HEIGHT, panelSx, SectionTitle, STALE_OPACITY, SummaryRow, useColors } from "./ui";

export function MobileSubmitBar({ derived, isSaving }) {
    const colors = useColors();
    const theme = useTheme();
    return (
        <Box
            sx={{
                display: { xs: "flex", md: "none" },
                position: "fixed",
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: theme.zIndex.appBar,
                minHeight: MOBILE_BAR_HEIGHT,
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
                px: 2,
                pb: "env(safe-area-inset-bottom)",
                backgroundColor: colors.primary[400],
                borderTop: `1px solid ${colors.grey[700]}`,
            }}
        >
            <Box sx={{ opacity: derived.stale ? STALE_OPACITY : 1 }}>
                <Typography variant="body2" color={colors.grey[300]}>
                    {derived.stale ? "Total (out of date)" : "Total"}
                </Typography>
                <Typography variant="h4" fontWeight={700}>
                    {formatMoney(derived.specialRate)}
                </Typography>
            </Box>
            <CreateBookingButton isSaving={isSaving} />
        </Box>
    );
}

function SummaryPanel({ derived, basis, submitted, serverError, problemRef, isSaving, field, onPatch }) {
    const colors = useColors();
    const basisLabelId = useId();
    const {
        nights,
        nightCount,
        datesValid,
        stale,
        capacity,
        totalGuests,
        specialRate,
        extrasTotal,
        basisRate,
        formProblem,
        errorCount,
    } = derived;
    const current = BASES[basis];

    return (
        <Box sx={{ ...panelSx(colors), position: { md: "sticky" }, top: { md: 16 } }}>
            <SectionTitle>Summary</SectionTitle>
            <Stack spacing={1.5}>
                {stale && (
                    <Alert severity="warning">
                        Out of date for the new dates. Select "Check again" to update the total.
                    </Alert>
                )}
                <SummaryRow
                    label="Nights"
                    value={stale ? (datesValid ? `${nightCount} (new dates)` : "—") : nights.length}
                />
                <Box sx={{ opacity: stale ? STALE_OPACITY : 1 }}>
                    <Stack spacing={1.5}>
                        <SummaryRow label="Rooms" value={capacity.rooms} />
                        <SummaryRow label="Guests" value={totalGuests} />
                        {extrasTotal > 0 && <SummaryRow label="Extra bed and meal" value={formatMoney(extrasTotal)} />}
                        <Box>
                            <Typography variant="body2" color={colors.grey[300]}>
                                Special rate (total, incl. GST{extrasTotal > 0 ? " and extras" : ""})
                            </Typography>
                            <Typography variant="h2" fontWeight={700} aria-live="polite">
                                {formatMoney(specialRate)}
                            </Typography>
                        </Box>
                    </Stack>
                </Box>

                <Box>
                    <Typography id={basisLabelId} variant="body2" color={colors.grey[300]} mb={0.5}>
                        Commission basis
                    </Typography>
                    <ButtonGroup fullWidth disableElevation size="small" role="group" aria-labelledby={basisLabelId}>
                        {Object.entries(BASES).map(([value, { label }]) => (
                            <Button
                                key={value}
                                variant={basis === value ? "contained" : "outlined"}
                                aria-pressed={basis === value}
                                onClick={() => onPatch({ basis: value })}
                                sx={focusRing(colors)}
                            >
                                <b>{label}</b>
                            </Button>
                        ))}
                    </ButtonGroup>
                </Box>

                <CssTextField
                    key={basis}
                    fullWidth
                    required
                    size="small"
                    type="number"
                    label={current.rateLabel}
                    value={basisRate}
                    onChange={(e) => onPatch({ [current.field]: e.target.value })}
                    inputProps={{ min: 0, step: "any" }}
                    {...field(basisRateId(basis))}
                />

                <Box ref={problemRef} tabIndex={-1} sx={{ outline: "none" }}>
                    <Stack spacing={1}>
                        {submitted && (formProblem || errorCount > 0) && (
                            <Alert severity="error">
                                {formProblem ||
                                    `${errorCount} field${errorCount === 1 ? " needs" : "s need"} attention. Fix the highlighted fields, then try again.`}
                            </Alert>
                        )}
                        {serverError && (
                            <Alert severity="error" role="alert">
                                {serverError}
                            </Alert>
                        )}
                    </Stack>
                </Box>

                <CreateBookingButton fullWidth isSaving={isSaving} />
            </Stack>
        </Box>
    );
}

export default SummaryPanel;
